// src/features/schedule/components/CurrentScheduleItemCard.tsx
import React, { useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { ScheduleItem, isTimeBox, isEvent } from '../../../types/scheduleItem';
import { format } from 'date-fns';
import { Sparkles, Clock, MapPin, ChevronRight } from '../../../components/common/icons';
import TimeBoxPattern from '../../../components/common/TimeBoxPattern';
import TimeFlowAnimation from '../../../components/common/TimeFlowAnimation';
import RelatedTasksSection from './RelatedTasksSection';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useCurrentTime } from '../../../hooks/useCurrentTime';
import { TimeBoxCategory } from '../../../types/timeBox';
import { EventCategory } from '../../../types/calendar-event';
import {
  getTimeBoxCategoryConfig,
  getEventCategoryConfig
} from '../../../constants/theme';
import { formatDuration } from '../../../utils/formatters';
import { UrgencyLevel } from './TimeDisplay';
import { QuickCreateModal } from '..';
import { TipTooltip } from '../../onboarding';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { DayPhase } from '../../../hooks/useDayPhase';
import { Task } from '../../../types/task';
import { motion } from 'framer-motion';
import { formatRelativeDate } from '../../../utils/dateUtils';

import MobileDots from './MobileDots';

interface CurrentScheduleItemCardProps {
  currentItem: ScheduleItem | null;
  nextItem: ScheduleItem | null;
  isLoading?: boolean;
  onAdjustTimeBox?: (id: string) => void;
  onAdjustEvent?: (id: string) => void;
  onCreateNewTimeBox?: () => void;
  onGetAISuggestion?: () => void;
  onRefresh?: () => void;
  // Onboarding Tips
  showAISuggestionTip?: boolean;
  onDismissAISuggestionTip?: () => void;
  // 三段式時間感知
  dayPhase?: DayPhase;
  openTaskCount?: number;
  completedTimeboxCount?: number;
  closedTodayTaskCount?: number;
  onOpenDayClose?: () => void;
  topOpenTasks?: Task[];
}

const CurrentScheduleItemCard: React.FC<CurrentScheduleItemCardProps> = ({
  currentItem,
  nextItem,
  isLoading,
  onAdjustTimeBox,
  onAdjustEvent,
  onGetAISuggestion,
  onRefresh,
  showAISuggestionTip,
  onDismissAISuggestionTip,
  dayPhase = 'active',
  openTaskCount = 0,
  completedTimeboxCount = 0,
  closedTodayTaskCount = 0,
  onOpenDayClose,
  topOpenTasks = [],
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const aiSuggestionBtnRef = useRef<HTMLButtonElement>(null);

  // 桌面版計時器內框動畫 - 測量尺寸以計算 SVG stroke-dasharray 周長
  const timerInnerRef = useRef<HTMLDivElement>(null);
  const [timerDims, setTimerDims] = useState<{
    w: number; h: number; half: number; full: number;
  } | null>(null);

  const [quickModalData, setQuickModalData] = useState<{ title?: string } | null>(null);

  // 剩餘時間計算 - 每分鐘自動更新
  const currentTime = useCurrentTime();

  const remainingMs = useMemo(() => {
    if (!currentItem?.endTime) return null;
    const endTime = new Date(currentItem.endTime);
    return Math.max(0, endTime.getTime() - currentTime.getTime());
  }, [currentItem?.endTime, currentTime]);

  const remainingText = remainingMs !== null ? formatDuration(remainingMs) : null;

  // remainingText 從 null 變有值時，Timer block 才插入 DOM，需重新 measure
  // 用 hasTimer 布林值當 dep，避免每分鐘 remainingText 字串變化都重建 observer
  const hasTimer = remainingText !== null;
  useLayoutEffect(() => {
    if (!hasTimer) return;
    const el = timerInnerRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        const rx = 8; // rounded-lg = 8px
        const perimeter = 2 * (width + height - 4 * rx) + 2 * Math.PI * rx;
        setTimerDims({ w: width, h: height, half: perimeter / 2, full: perimeter });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasTimer]);

  // 三級緊急狀態（取代舊的 isEndingSoon 二元邏輯）
  const urgencyLevel: UrgencyLevel = useMemo(() => {
    if (remainingMs === null) return 'normal';
    if (remainingMs <= 60 * 1000) return 'critical';  // 1 分鐘內
    if (remainingMs <= 5 * 60 * 1000) return 'warning'; // 5 分鐘內
    return 'normal';
  }, [remainingMs]);

  // 計算進度百分比（已過時間 / 總時間）
  const elapsedPercent = useMemo(() => {
    if (!currentItem?.startTime || !currentItem?.endTime || remainingMs === null) return 0;
    const totalMs = new Date(currentItem.endTime).getTime() - new Date(currentItem.startTime).getTime();
    if (totalMs <= 0) return 0;
    const elapsed = totalMs - remainingMs;
    return Math.min(100, Math.max(0, (elapsed / totalMs) * 100));
  }, [currentItem?.startTime, currentItem?.endTime, remainingMs]);

  // 獲取 Pattern 類別
  const getPatternCategory = (item: ScheduleItem): TimeBoxCategory | EventCategory | 'empty' => {
    if (isTimeBox(item)) {
      return item.category;
    }
    if (isEvent(item)) {
      return item.category;
    }
    return 'empty';
  };

  // 處理卡片點擊 - 直接進入編輯（必須在條件返回之前定義）
  const handleCardClick = useCallback(() => {
    if (!currentItem) return;
    if (isTimeBox(currentItem) && onAdjustTimeBox) {
      onAdjustTimeBox(currentItem.id);
    } else if (isEvent(currentItem) && onAdjustEvent) {
      onAdjustEvent(currentItem.id);
    }
  }, [currentItem, onAdjustTimeBox, onAdjustEvent]);

  // 載入中骨架屏 - 避免在資料就緒前閃爍空狀態
  if (isLoading && !currentItem) {
    return (
      <div className="relative overflow-hidden py-8 px-5 md:py-14 md:px-10 rounded-xl md:rounded-1.5xl bg-white border border-gray-200 animate-pulse">
        <div className="space-y-4">
          <div className="h-3 w-24 bg-gray-200 rounded" />
          <div className="h-6 w-48 bg-gray-200 rounded" />
          <div className="h-4 w-32 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  // 空狀態（三段式時間感知）
  if (!currentItem) {
    // ── Morning ──────────────────────────────────────────
    if (dayPhase === 'morning') {
      const subtext = openTaskCount > 0
        ? `You have ${openTaskCount} task${openTaskCount > 1 ? 's' : ''} waiting`
        : 'A clear morning — make it count';

      return (
        <div className="relative overflow-hidden py-8 px-5 md:py-12 md:px-10 rounded-xl md:rounded-1.5xl bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border border-amber-100 z-content">
          <TimeBoxPattern category="empty" opacity={0.05} />

          <div className="relative z-content">
            <p className="text-sm font-medium text-amber-600 mb-1">Good morning</p>
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-1" role="status" aria-live="polite">
              Nothing scheduled right now
            </h2>
            <p className="text-gray-500 text-sm mb-7">{subtext}</p>

            <div className={`${isDesktop ? 'flex gap-3 items-center' : 'space-y-3'}`}>
              <div className={`relative ${isDesktop ? 'inline-block' : 'w-full'}`}>
                <button
                  ref={aiSuggestionBtnRef}
                  className={`btn-primary ${isDesktop ? '' : 'w-full'}`}
                  onClick={onGetAISuggestion}
                >
                  <Sparkles size={14} />
                  Plan my morning
                </button>
                {showAISuggestionTip && onDismissAISuggestionTip && (
                  <TipTooltip
                    tipId="today-ai-plan"
                    targetRef={aiSuggestionBtnRef}
                    onClose={onDismissAISuggestionTip}
                  />
                )}
              </div>
              <button
                className={`btn-secondary ${isDesktop ? '' : 'w-full'}`}
                onClick={() => {
                  eventBus.publish(EVENTS.UI.TODAY_QUICK_CREATE_OPENED);
                  setQuickModalData({});
                }}
              >
                Quick Create
              </button>
            </div>
          </div>

          <QuickCreateModal
            isOpen={quickModalData !== null}
            onClose={() => setQuickModalData(null)}
            onSuccess={() => { if (onRefresh) onRefresh(); }}
          />
        </div>
      );
    }

    // ── Evening ──────────────────────────────────────────
    if (dayPhase === 'evening') {
      return (
        <div className="relative overflow-hidden py-8 px-5 md:py-12 md:px-10 rounded-xl md:rounded-1.5xl bg-gradient-to-br from-sky-950 via-slate-900 to-sky-900 border border-sky-800/50 z-content">
          {/* 裝飾星點 */}
          <div aria-hidden="true">
            <span className="absolute top-4 left-8 w-1 h-1 rounded-full bg-white opacity-30" />
            <span className="absolute top-8 left-20 w-0.5 h-0.5 rounded-full bg-white opacity-20" />
            <span className="absolute top-5 right-16 w-1 h-1 rounded-full bg-white opacity-40" />
            <span className="absolute bottom-6 right-24 w-0.5 h-0.5 rounded-full bg-white opacity-25" />
            <span className="absolute bottom-8 left-12 w-1 h-1 rounded-full bg-white opacity-20" />
          </div>

          <div className="relative z-content">
            <p className="text-sm font-medium text-sky-300 mb-1">Evening</p>
            <h2 className="text-lg md:text-xl font-semibold text-white mb-4" role="status" aria-live="polite">
              Today's schedule is complete
            </h2>

            {/* 今日摘要 */}
            <div className="flex items-center gap-4 mb-7">
              {completedTimeboxCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                  <span className="text-sm text-sky-200">
                    {completedTimeboxCount} timebox{completedTimeboxCount > 1 ? 'es' : ''}
                  </span>
                </div>
              )}
              {closedTodayTaskCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                  <span className="text-sm text-sky-200">
                    {closedTodayTaskCount} task{closedTodayTaskCount > 1 ? 's' : ''} done
                  </span>
                </div>
              )}
              {completedTimeboxCount === 0 && closedTodayTaskCount === 0 && (
                <span className="text-sm text-sky-300/60">Rest well tonight</span>
              )}
            </div>

            <button
              className="btn-secondary opacity-80 hover:opacity-100 border-sky-600/50 text-sky-200 bg-sky-800/30 hover:bg-sky-700/40"
              onClick={onOpenDayClose}
            >
              Close your day
            </button>
          </div>
        </div>
      );
    }

    // ── Active Day（default）────────────────────────────
    // 計算今天已過的時間比例，供時間軸 bar 使用
    const now = new Date();
    const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now); endOfDay.setHours(23, 59, 59, 999);
    const dayProgress = (now.getTime() - startOfDay.getTime()) / (endOfDay.getTime() - startOfDay.getTime());

    const handleTaskClick = (task: Task) => {
      eventBus.publish(EVENTS.UI.TODAY_QUICK_CREATE_OPENED);
      setQuickModalData({ title: task.title });
    };

    const handleQuickCreateClick = () => {
      eventBus.publish(EVENTS.UI.TODAY_QUICK_CREATE_OPENED);
      setQuickModalData({});
    };

    return (
      <div className="relative overflow-hidden py-8 px-5 md:py-12 md:px-10 rounded-xl md:rounded-1.5xl bg-white border border-gray-200 z-content">
        <TimeBoxPattern category="empty" opacity={0.04} />

        <div className="relative z-content">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-1" role="status" aria-live="polite">
            You're in a free window
          </h2>

          {/* 時間軸 bar */}
          <div className="mb-7 mt-3" aria-hidden="true">
            <div className="relative h-1.5 bg-gray-100 rounded-full overflow-visible">
              <motion.div
                className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-sky-300 to-sky-400"
                initial={{ width: 0 }}
                animate={{ width: `${dayProgress * 100}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
              <motion.div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                style={{ left: `${dayProgress * 100}%` }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <motion.div
                  className="w-3 h-3 rounded-full bg-sky-500 border-2 border-white shadow-sm"
                  animate={{ scale: [1, 1.25, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                />
              </motion.div>
            </div>
            <div className="flex justify-between mt-1.5 text-[10px] text-gray-300 font-medium">
              <span>12 AM</span>
              <span>12 PM</span>
              <span>11 PM</span>
            </div>
          </div>

          {/*
            桌面版：左右分欄
              左欄（flex-1）— Pick something（task list）
              右欄（固定寬）— Or let AI decide + Something new

            行動版：單欄垂直流（flex-col）
          */}
          <div className="flex flex-col md:flex-row" style={isDesktop ? { gap: '2rem' } : undefined}>

            {/* ── 左欄 / 行動版全寬：Pick something ── */}
            {topOpenTasks.length > 0 && (
              <div className="flex-1 mb-6 md:mb-0">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Pick one to move forward
                </p>
                <div className="flex flex-col gap-1">
                  {topOpenTasks.map((task) => {
                    const dateStr = task.targetDate || task.plan;
                    const dateLabel = dateStr ? formatRelativeDate(dateStr) : null;
                    const isOverdue = dateLabel?.startsWith('Overdue');
                    const isToday = dateLabel === 'Today';

                    return (
                      <button
                        key={task.id}
                        className="w-full flex items-center justify-between py-2.5 px-3 rounded-lg border border-gray-100 hover:border-sky-200 hover:bg-sky-50 transition-colors text-left group"
                        onClick={() => handleTaskClick(task)}
                        aria-label={`Timebox "${task.title}"`}
                      >
                        <span className="text-sm text-gray-700 font-medium truncate flex-1 mr-3 group-hover:text-sky-700 transition-colors">
                          {task.title}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {dateLabel && (
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${
                              isOverdue
                                ? 'bg-red-50 text-red-500'
                                : isToday
                                  ? 'bg-sky-50 text-sky-500'
                                  : 'bg-gray-100 text-gray-400'
                            }`}>
                              {dateLabel}
                            </span>
                          )}
                          <ChevronRight size={14} className="text-gray-300 group-hover:text-sky-400 transition-colors" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 桌面版垂直分隔線（左右欄之間） */}
            {topOpenTasks.length > 0 && isDesktop && (
              <div className="w-px bg-gray-100 flex-shrink-0" />
            )}

            {/* ── 右欄 / 行動版全寬：AI decide + Quick Create ── */}
            <div className={topOpenTasks.length > 0 ? 'w-52 flex-shrink-0 flex flex-col gap-3' : 'w-full flex flex-col gap-3'}>

              {/* 主 CTA：What's next? */}
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Let Orlo suggest
              </p>
              <div className="relative">
                <button
                  ref={aiSuggestionBtnRef}
                  className="btn-primary w-full"
                  onClick={onGetAISuggestion}
                >
                  <Sparkles size={14} />
                  What's next?
                </button>
                {showAISuggestionTip && onDismissAISuggestionTip && (
                  <TipTooltip
                    tipId="today-ai-plan"
                    targetRef={aiSuggestionBtnRef}
                    onClose={onDismissAISuggestionTip}
                  />
                )}
              </div>

              {/* 次要逃生口：Quick Create */}
              <button
                className="w-full text-sm text-gray-400 hover:text-sky-600 font-medium py-2 px-3 rounded-lg hover:bg-sky-50 transition-colors text-left"
                onClick={handleQuickCreateClick}
              >
                + Quick Create
              </button>

            </div>
          </div>
        </div>

        <QuickCreateModal
          isOpen={quickModalData !== null}
          onClose={() => setQuickModalData(null)}
          onSuccess={() => { if (onRefresh) onRefresh(); }}
          initialData={quickModalData ?? undefined}
        />
      </div>
    );
  }

  // 顯示當前項目
  const formattedTimeRange = currentItem.endTime
    ? `${format(new Date(currentItem.startTime), 'HH:mm')}–${format(new Date(currentItem.endTime), 'HH:mm')}`
    : format(new Date(currentItem.startTime), 'HH:mm');

  const patternCategory = getPatternCategory(currentItem);
  const patternOpacity = isEvent(currentItem) ? 0.08 : 0.06;

  // meta 標籤資訊
  let categoryConfig;
  let scheduleTypeLabel: string;
  if (isEvent(currentItem)) {
    categoryConfig = getEventCategoryConfig(currentItem.category);
    scheduleTypeLabel = 'Event';
  } else if (isTimeBox(currentItem)) {
    categoryConfig = getTimeBoxCategoryConfig(currentItem.category);
    scheduleTypeLabel = 'Timebox';
  } else {
    categoryConfig = { label: 'Unknown', color: '#64748B', dotColor: '#64748B' };
    scheduleTypeLabel = 'Unknown';
  }

  // Timer urgency 色系
  // 軌道描邊顏色（urgency 對應的 indigo/amber/red 200）
  const orbitStrokeColor: Record<UrgencyLevel, string> = {
    normal: '#C7D2FE', // sky-200
    warning: '#FDE68A', // amber-200
    critical: '#FECACA', // red-200
  };

  const timerColorMap: Record<UrgencyLevel, { text: string; bg: string; border: string }> = {
    normal: { text: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200' },
    warning: { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    critical: { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  };
  const timerColors = timerColorMap[urgencyLevel];

  const noteText = currentItem.notes;

  return (
    <div
      className="relative overflow-hidden rounded-xl md:rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer border border-black/[0.06]"
      onClick={handleCardClick}
    >
      <TimeBoxPattern category={patternCategory} opacity={patternOpacity} />

      {/* card-top：左側資訊 + 右側 Timer（桌面並排 / 行動疊加）*/}
      <div className="relative z-content flex items-start gap-4 p-5 md:p-7 pb-3 md:pb-4">
        {/* 左欄：meta + 標題 + 時間範圍 */}
        <div className="flex-1 min-w-0">
          {/* meta row */}
          <div className="flex items-center gap-1.5 mb-3">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: categoryConfig.dotColor }}
            />
            <span
              className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: categoryConfig.color }}
            >
              {scheduleTypeLabel}
            </span>
            <span className="text-[11px] text-gray-300 mx-0.5">·</span>
            <span
              className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: categoryConfig.color }}
            >
              {categoryConfig.label}
            </span>
          </div>

          {/* 標題 */}
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 leading-snug mb-3">
            {currentItem.title}
          </h2>

          {/* 時間範圍 + 位置 */}
          <div className="flex items-center gap-4 text-gray-500">
            <div className="flex items-center gap-1.5">
              <Clock size={13} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm">{formattedTimeRange}</span>
            </div>
            {currentItem.location && (
              <div className="flex items-center gap-1.5">
                <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-sm">{currentItem.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* 桌面版 Timer block（行動版隱藏） */}
        {remainingText && (
          <div
            className={`hidden md:flex flex-shrink-0 flex-col items-center justify-center p-1 rounded-xl w-[180px] ${timerColors.bg}`}
            role="timer"
            aria-label={`${remainingText} remaining`}
            aria-live="polite"
          >
            {/* 內層：軌道動畫描邊 */}
            <div
              ref={timerInnerRef}
              className="relative w-full flex flex-col items-center justify-center px-3 py-3 rounded-lg"
            >
              {/* SVG 軌道列車動畫：半圈描邊順時鐘繞行，每 5 秒到達對側 */}
              {timerDims && (
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox={`0 0 ${timerDims.w} ${timerDims.h}`}
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="0.5" y="0.5"
                    width={timerDims.w - 1}
                    height={timerDims.h - 1}
                    rx="13"
                    stroke={orbitStrokeColor[urgencyLevel]}
                    strokeWidth="1"
                    strokeDasharray={`${timerDims.half} ${timerDims.half}`}
                  >
                    {/* 負值 dashoffset = 反向移動 = 順時鐘 */}
                    <animate
                      attributeName="stroke-dashoffset"
                      values={`0; ${timerDims.half}; ${timerDims.full}`}
                      keyTimes="0; 0.5; 1"
                      calcMode="spline"
                      keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
                      dur="10s"
                      repeatCount="indefinite"
                    />
                  </rect>
                </svg>
              )}
              <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
                Remaining
              </span>
              <span className={`text-3xl font-bold tabular-nums leading-none ${timerColors.text}`}>
                {remainingText}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* card-body：description + tasks */}
      <div className="relative z-content px-5 md:px-7 pb-6">
        {/* Description（italic + gold left-border） */}
        {noteText && (
          <p className="text-sm text-gray-500 leading-relaxed mb-5 pl-3 border-l-2 border-sky-200/70">
            {noteText}
          </p>
        )}

        {/* Related Tasks - 僅 TimeBox 顯示 */}
        {isTimeBox(currentItem) && currentItem.taskIds && currentItem.taskIds.length > 0 && (
          <RelatedTasksSection taskIds={currentItem.taskIds} />
        )}
      </div>

      {/* 行動版 Timer Band（桌面隱藏） */}
      {remainingText && (
        <div
          className={`md:hidden flex items-center justify-between mx-5 mb-4 px-5 py-3.5 rounded-xl ${timerColors.bg}`}
          role="timer"
          aria-label={`${remainingText} remaining`}
          aria-live="polite"
        >
          {/* 左側：Remaining 與 小點動畫 */}
          <div className="flex flex-col justify-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400/90 leading-none">
              Remaining
            </span>
            <div className="flex items-end mt-0.5">
              <MobileDots dotColor={orbitStrokeColor[urgencyLevel]} />
            </div>
          </div>

          {/* 右側：時間垂直置中 */}
          <div className={`text-[32px] font-bold tabular-nums leading-none tracking-tight ${timerColors.text}`}>
            {remainingText}
          </div>
        </div>
      )}

      {/* 底部 progress bar */}
      <div className="relative z-content px-5 md:px-7 pb-5">
        <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-slate-600 transition-[width] duration-1000"
            style={{ width: `${elapsedPercent}%` }}
          />
        </div>
      </div>

    </div>
  );
};

export default CurrentScheduleItemCard;
